import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL, apiUrl } from '../config/api';

// Component for Operator Concedii (Vacations)
const OperatorConcedii = ({ currentUser, currentUserRole, operatorId, goBack }) => {
  const [operator, setOperator] = useState(null);
  const [concedii, setConcedii] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingConcediu, setEditingConcediu] = useState(null);
  const [dataInceput, setDataInceput] = useState('');
  const [dataSfarsit, setDataSfarsit] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  
  // Tratăm atât 'Admin' cât și 'Administrator' ca rol de administrator
  const isAdmin = currentUserRole === 'Admin' || currentUserRole === 'Administrator';
  // Operator role helper
  const isOperator = currentUserRole === 'Operatori' || currentUserRole === 'Operator';

  useEffect(() => {
    if (!operatorId) {
      console.error('No operator ID provided');
      setError('ID operator lipsă');
      setLoading(false);
      return;
    }
    
    console.log(`Loading operator data for ID: ${operatorId}`);
    setLoading(true);
    
    // First fetch the operator details with explicit error handling
    const fetchOperatorDetails = async () => {
      try {
        // Verify operatorId is a valid number
        const numericOperatorId = Number(operatorId);
        if (isNaN(numericOperatorId)) {
          throw new Error('ID-ul operatorului nu este valid');
        }
        
        console.log(`Fetching operator: GET ${API_BASE_URL}/operatori/${numericOperatorId}`);
        const operatorResponse = await axios.get(`${API_BASE_URL}/operatori/${numericOperatorId}`);
        console.log('Operator data response:', operatorResponse.data);
        
        if (!operatorResponse.data) {
          throw new Error(`Operatorul cu ID ${numericOperatorId} nu a fost găsit`);
        }
        
        setOperator(operatorResponse.data);
        
        // Then fetch concedii for this operator
        console.log(`Fetching concedii: GET ${API_BASE_URL}/operatori/${numericOperatorId}/concedii`);
        const concediiResponse = await axios.get(`${API_BASE_URL}/operatori/${numericOperatorId}/concedii`);
        console.log('Concedii data response:', concediiResponse.data);
        
        setConcedii(concediiResponse.data || []);
        setError('');
      } catch (err) {
        console.error('Error in operator concedii flow:', err);
        console.error('Error details:', err.response?.data);
        console.error('Status code:', err.response?.status);
        
        if (err.response?.status === 404) {
          setError(`Eroare: Operatorul cu ID ${operatorId} nu a fost găsit`);
        } else {
          setError(`Eroare: ${err.response?.data?.error || err.message}`);
        }
        
        // Clear operator data in case of error
        setOperator(null);
        setConcedii([]);
      } finally {
        setLoading(false);
      }
    };
    
    fetchOperatorDetails();
  }, [operatorId]);

  // Refresh concedii data
  const fetchConcedii = async () => {
    if (!operatorId) {
      console.error('No operator ID provided for fetchConcedii');
      return;
    }
    
    try {
      setLoading(true);
      console.log(`Refreshing concedii: GET ${API_BASE_URL}/operatori/${operatorId}/concedii`);
      const response = await axios.get(`${API_BASE_URL}/operatori/${operatorId}/concedii`);
      console.log('Refreshed concedii data:', response.data);
      setConcedii(response.data || []);
      setError('');
    } catch (error) {
      console.error('Error refreshing vacations:', error);
      console.error('Error details:', error.response?.data);
      console.error('Status code:', error.response?.status);
      // Don't show error message here as it might be called after showing another error
    } finally {
      setLoading(false);
    }
  };

  const handleAddConcediu = () => {
    setEditingConcediu(null);
    setDataInceput('');
    setDataSfarsit('');
    setShowModal(true);
    setError('');
  };

  const handleEditConcediu = (concediu) => {
    if (isOperator) { setError('Operatorii nu pot edita concedii'); return; }
    setEditingConcediu(concediu);
    setDataInceput(concediu.dataInceput);
    setDataSfarsit(concediu.dataSfarsit);
    setShowModal(true);
    setError('');
  };

  const handleDeleteConcediu = async (concediu) => {
    if (isOperator) { setError('Operatorii nu pot șterge concedii'); return; }
    if (window.confirm(`Sigur doriți să ștergeți concediul din perioada ${formatDate(concediu.dataInceput)} - ${formatDate(concediu.dataSfarsit)}?`)) {
      try {
        setLoading(true);
        await axios.delete(`${API_BASE_URL}/operatori-concedii/${concediu.id}`, {
          data: { currentUser: currentUser?.username }
        });
        await fetchConcedii();
        setSuccess('Concediul a fost șters cu succes');
        setTimeout(() => setSuccess(''), 3000);
      } catch (error) {
        console.error('Error deleting vacation:', error);
        setError(error.response?.data?.error || 'Eroare la ștergerea concediului');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleSave = async () => {
    if (!dataInceput || !dataSfarsit) {
      setError('Ambele date sunt obligatorii');
      return;
    }

    if (new Date(dataInceput) > new Date(dataSfarsit)) {
      setError('Data de început nu poate fi după data de sfârșit');
      return;
    }

    try {
      setLoading(true);
      setError('');

      // Ensure we have valid data
      if (!operatorId) {
        throw new Error('ID operator lipsă');
      }

      if (!currentUser?.username) {
        throw new Error('Utilizator necunoscut');
      }

      // Ensure operatorId is a valid number
      const numericOperatorId = parseInt(operatorId, 10);
      if (isNaN(numericOperatorId)) {
        throw new Error(`ID operator invalid: ${operatorId}`);
      }
      
      const concediuData = {
        operatorId: numericOperatorId,
        dataInceput,
        dataSfarsit,
        createdBy: currentUser?.username,
        currentUser: currentUser?.username
      };

      console.log('Saving concediu with data:', concediuData);

      if (editingConcediu) {
        // Edit existing vacation
        console.log(`Updating concediu: PUT ${API_BASE_URL}/operatori-concedii/${editingConcediu.id}`);
        try {
          const response = await axios.put(`${API_BASE_URL}/operatori-concedii/${editingConcediu.id}`, concediuData);
          console.log('Update response:', response.data);
          setSuccess('Concediul a fost actualizat cu succes');
        } catch (updateError) {
          console.error('Error updating concediu:', updateError);
          throw new Error(updateError.response?.data?.error || 'Eroare la actualizarea concediului');
        }
      } else {
        // Add new vacation
        console.log(`Creating concediu: POST ${API_BASE_URL}/operatori-concedii`);
        try {
          const response = await axios.post(`${API_BASE_URL}/operatori-concedii`, concediuData);
          console.log('Create response:', response.data);
          setSuccess('Concediul a fost adăugat cu succes');
        } catch (createError) {
          console.error('Error creating concediu:', createError);
          const errorMessage = createError.response?.data?.error || 'Eroare la crearea concediului';
          throw new Error(errorMessage);
        }
      }

      await fetchConcedii();
      setShowModal(false);
      setDataInceput('');
      setDataSfarsit('');
      setEditingConcediu(null);
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error saving vacation:', error);
      setError(error.response?.data?.error || 'Eroare la salvarea concediului');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setShowModal(false);
    setDataInceput('');
    setDataSfarsit('');
    setEditingConcediu(null);
    setError('');
  };

  const handlePrevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  const getDaysInMonth = (month, year) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (month, year) => {
    return new Date(year, month, 1).getDay();
  };

  const formatDate = (dateString) => {
    const options = { day: '2-digit', month: '2-digit', year: 'numeric' };
    return new Date(dateString).toLocaleDateString('ro-RO', options);
  };

  const monthNames = [
    'Ianuarie', 'Februarie', 'Martie', 'Aprilie', 'Mai', 'Iunie',
    'Iulie', 'August', 'Septembrie', 'Octombrie', 'Noiembrie', 'Decembrie'
  ];

  // Helper: parse a YYYY-MM-DD (or date string) into a local Date at midnight, avoiding timezone shift off-by-one
  const parseLocalYMD = (value) => {
    if (!value) return null;
    if (value instanceof Date) return new Date(value.getFullYear(), value.getMonth(), value.getDate());
    // Accept 'YYYY-MM-DD' optionally with time
    const pure = String(value).split('T')[0];
    const parts = pure.split('-');
    if (parts.length === 3) {
      const [y,m,d] = parts.map(Number);
      if (!isNaN(y) && !isNaN(m) && !isNaN(d)) {
        return new Date(y, m - 1, d); // local midnight
      }
    }
    const d = new Date(value);
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  };

  // --- Vacation working days (exclude weekends) summary logic ---
  const getWorkingDaysInclusive = (startDate, endDate) => {
    // Both are Date objects
    let count = 0;
    const d = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
    while (d <= endDate) {
      const day = d.getDay(); // 0=Sun 6=Sat
      if (day !== 0 && day !== 6) count++;
      d.setDate(d.getDate() + 1);
    }
    return count;
  };

  const computeYearlyVacationStats = (year) => {
    if (!concedii || concedii.length === 0) return { total: 0, details: [] };
    const yearStart = new Date(year, 0, 1);
    const yearEnd = new Date(year, 11, 31);
    const details = concedii.map(c => {
      const start = new Date(c.dataInceput);
      const end = new Date(c.dataSfarsit);
      if (end < yearStart || start > yearEnd) return null; // no overlap
      const effectiveStart = start < yearStart ? yearStart : start;
      const effectiveEnd = end > yearEnd ? yearEnd : end;
      const workingDays = getWorkingDaysInclusive(effectiveStart, effectiveEnd);
      return { id: c.id, original: c, effectiveStart, effectiveEnd, workingDays };
    }).filter(Boolean);
    const total = details.reduce((sum, d) => sum + d.workingDays, 0);
    return { total, details };
  };

  const nowYear = (new Date()).getFullYear();
  const isViewingCurrentYear = currentYear === nowYear;
  const yearlyStats = computeYearlyVacationStats(currentYear);

  const isConcediuDay = (day) => {
    const date = new Date(currentYear, currentMonth, day); // local midnight
    const compare = parseLocalYMD(date);
    return concedii.some(concediu => {
      const startDate = parseLocalYMD(concediu.dataInceput);
      const endDate = parseLocalYMD(concediu.dataSfarsit);
      if (!startDate || !endDate) return false;
      return compare >= startDate && compare <= endDate;
    });
  };

  const getConcediuForDay = (day) => {
    const date = new Date(currentYear, currentMonth, day);
    const compare = parseLocalYMD(date);
    return concedii.find(concediu => {
      const startDate = parseLocalYMD(concediu.dataInceput);
      const endDate = parseLocalYMD(concediu.dataSfarsit);
      if (!startDate || !endDate) return false;
      return compare >= startDate && compare <= endDate;
    });
  };

  const isWeekend = (day) => {
    const dow = new Date(currentYear, currentMonth, day).getDay(); // 0=Sun 6=Sat
    return dow === 0 || dow === 6;
  };

  const canUserEdit = () => {
    return !isOperator;
  };

  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(currentMonth, currentYear);
    const firstDayOfMonth = getFirstDayOfMonth(currentMonth, currentYear);
    
    // Adjust first day of week to start with Monday (in JS, 0 = Sunday)
    // If Sunday (0), make it position 6, otherwise subtract 1
    const firstDayAdjusted = firstDayOfMonth === 0 ? 6 : firstDayOfMonth - 1;
    
    // Create calendar weeks array (array of rows)
    const weeks = [];
    let week = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < firstDayAdjusted; i++) {
      week.push(<td key={`empty-start-${i}`} className="empty-day"></td>);
    }
    
    // Add cells for all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const isVacation = isConcediuDay(day);
      const concediu = isVacation ? getConcediuForDay(day) : null;
      
      week.push(
        <td 
          key={`day-${day}`}
          className={`calendar-day ${isVacation ? 'vacation-day' : ''}`}
          onClick={() => isVacation && canUserEdit(concediu) ? handleEditConcediu(concediu) : null}
          style={{ 
            cursor: isVacation && canUserEdit(concediu) ? 'pointer' : 'default',
            backgroundColor: isVacation ? '#ffe0b2' : '',
            position: 'relative'
          }}
        >
          <div 
            className="day-number" 
            style={isWeekend(day) ? { color: '#d20000', fontWeight: 600 } : undefined}
          >
            {day}
          </div>
          {isVacation && (
            <div 
              className="vacation-indicator"
              title={`${formatDate(concediu.dataInceput)} - ${formatDate(concediu.dataSfarsit)}`}
            ></div>
          )}
        </td>
      );
      
      // If we've reached the end of a week, push the current week and start a new one
      if ((firstDayAdjusted + day) % 7 === 0) {
        weeks.push(<tr key={`week-${weeks.length}`}>{week}</tr>);
        week = [];
      }
    }
    
    // If there are remaining days, add empty cells to complete the week
    if (week.length > 0) {
      const emptyCellsNeeded = 7 - week.length;
      for (let i = 0; i < emptyCellsNeeded; i++) {
        week.push(<td key={`empty-end-${i}`} className="empty-day"></td>);
      }
      weeks.push(<tr key={`week-${weeks.length}`}>{week}</tr>);
    }
    
    return weeks;
  };

  // The goBack function is now passed as a prop

  // Simple loading state
  if (loading && !operator) {
    return (
      <div className="card">
        <h2>Se încarcă...</h2>
        <p>Vă rugăm să așteptați încărcarea datelor.</p>
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h2>📅 Concedii - {operator?.nume}</h2>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button className="btn btn-secondary" onClick={goBack}>
              ⬅️ Înapoi la Operatori
            </button>
            <button 
              className="btn btn-primary"
              onClick={handleAddConcediu}
              disabled={loading}
            >
              ➕ Adaugă Concediu
            </button>
          </div>
        </div>

        {error && (
          <div style={{ 
            background: '#f8d7da', 
            color: '#721c24', 
            padding: '0.75rem', 
            borderRadius: '4px', 
            marginBottom: '1rem',
            border: '1px solid #f5c6cb'
          }}>
            {error}
          </div>
        )}

        {success && (
          <div style={{ 
            background: '#d1e7dd', 
            color: '#0f5132', 
            padding: '0.75rem', 
            borderRadius: '4px', 
            marginBottom: '1rem',
            border: '1px solid #badbcc'
          }}>
            {success}
          </div>
        )}

        {/* Yearly working days summary (exclude weekends) */}
        {yearlyStats.details.length > 0 && (
          <div style={{
            background: '#f0f7ff',
            border: '1px solid #b3d9ff',
            padding: '0.75rem 1rem',
            borderRadius: '6px',
            marginBottom: '1rem'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem' }}>
              <div>
                <strong>Zile de concediu (fără weekend) în {currentYear}:</strong>
                <span style={{ marginLeft: '0.5rem', fontSize: '1.1rem', color: '#0056b3' }}>{yearlyStats.total}</span>
                {!isViewingCurrentYear && currentYear > nowYear && (
                  <span style={{ marginLeft: '0.75rem', fontSize: '0.8rem', background: '#ffe9b3', padding: '2px 6px', borderRadius: '4px', color: '#7a5200' }}>Planificat viitor</span>
                )}
                {!isViewingCurrentYear && currentYear < nowYear && (
                  <span style={{ marginLeft: '0.75rem', fontSize: '0.8rem', background: '#e2e3e5', padding: '2px 6px', borderRadius: '4px', color: '#50565c' }}>Istoric</span>
                )}
              </div>
              <div style={{ fontSize: '0.85rem', color: '#333' }}>
                Zile lucrătoare calculate excluzând sâmbătă și duminică. Concediile care traversează anii sunt tăiate la limitele anului afișat.
              </div>
            </div>
            {/* Breakdown table */}
            <div style={{ marginTop: '0.75rem', overflowX: 'auto' }}>
              <table className="table" style={{ margin: 0 }}>
                <thead>
                  <tr>
                    <th style={{ whiteSpace: 'nowrap' }}>Perioadă (ajustată în {currentYear})</th>
                    <th style={{ whiteSpace: 'nowrap' }}>Zile lucrătoare</th>
                    <th>Perioadă originală</th>
                  </tr>
                </thead>
                <tbody>
                  {yearlyStats.details.map(d => (
                    <tr key={d.id}>
                      <td>{formatDate(d.effectiveStart.toISOString())} - {formatDate(d.effectiveEnd.toISOString())}</td>
                      <td style={{ fontWeight: 600 }}>{d.workingDays}</td>
                      <td>{formatDate(d.original.dataInceput)} - {formatDate(d.original.dataSfarsit)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className="calendar-container">
          <div className="calendar-header">
            <button className="btn btn-outline" onClick={handlePrevMonth}>◀️</button>
            <h3>{monthNames[currentMonth]} {currentYear}</h3>
            <button className="btn btn-outline" onClick={handleNextMonth}>▶️</button>
          </div>

          <table className="calendar">
            <thead>
              <tr>
                <th>Luni</th>
                <th>Marți</th>
                <th>Miercuri</th>
                <th>Joi</th>
                <th>Vineri</th>
                <th>Sâmbătă</th>
                <th>Duminică</th>
              </tr>
            </thead>
            <tbody>
              {renderCalendar()}
            </tbody>
          </table>

          <div className="calendar-legend">
            <div className="legend-item">
              <div className="legend-color" style={{ backgroundColor: '#ffe0b2' }}></div>
              <div>Zile de concediu</div>
            </div>
          </div>
        </div>

        {concedii.length > 0 ? (
          <div style={{ marginTop: '2rem' }}>
            <h3>Lista concediilor</h3>
            <div style={{ overflowX: 'auto' }}>
              <table className="table">
                <thead>
                  <tr>
                    <th>Data început</th>
                    <th>Data sfârșit</th>
                    <th>Creat de</th>
                    <th>Acțiuni</th>
                  </tr>
                </thead>
                <tbody>
                  {concedii.map(concediu => (
                    <tr key={concediu.id}>
                      <td>{formatDate(concediu.dataInceput)}</td>
                      <td>{formatDate(concediu.dataSfarsit)}</td>
                      <td>{concediu.createdBy}</td>
                      <td>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <button
                            className={`btn ${canUserEdit(concediu) ? 'btn-warning' : 'btn-disabled'}`}
                            onClick={() => handleEditConcediu(concediu)}
                            disabled={!canUserEdit(concediu) || loading}
                            style={{ 
                              fontSize: '0.8rem', 
                              padding: '0.25rem 0.5rem',
                              opacity: canUserEdit(concediu) ? 1 : 0.5,
                              cursor: canUserEdit(concediu) ? 'pointer' : 'not-allowed'
                            }}
                          >
                            ✏️ Editează
                          </button>
                          <button
                            className={`btn ${canUserEdit(concediu) ? 'btn-danger' : 'btn-disabled'}`}
                            onClick={() => handleDeleteConcediu(concediu)}
                            disabled={!canUserEdit(concediu) || loading}
                            style={{ 
                              fontSize: '0.8rem', 
                              padding: '0.25rem 0.5rem',
                              background: canUserEdit(concediu) ? '#e74c3c' : '#ccc',
                              opacity: canUserEdit(concediu) ? 1 : 0.5,
                              cursor: canUserEdit(concediu) ? 'pointer' : 'not-allowed'
                            }}
                          >
                            🗑️ Șterge
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div style={{ textAlign: 'center', color: '#666', margin: '1rem 0' }}>
            {loading ? 'Se încarcă concediile...' : 'Nu există concedii adăugate pentru acest operator.'}
          </div>
        )}
      </div>

      {/* Modal for Add/Edit Concediu */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ 
            padding: '2rem',
            maxWidth: '400px',
            borderRadius: '8px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
          }}>
            <h3 style={{ 
              marginBottom: '1.5rem', 
              color: '#333',
              borderBottom: '2px solid #007bff',
              paddingBottom: '0.5rem'
            }}>
              {editingConcediu ? 'Editează Concediu' : 'Adaugă Concediu Nou'}
            </h3>
            
            <div className="form-group" style={{ marginBottom: '1.5rem' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '0.5rem', 
                fontWeight: '600',
                color: '#555'
              }}>
                Data început *
              </label>
              <input
                type="date"
                value={dataInceput}
                onChange={(e) => setDataInceput(e.target.value)}
                style={{ 
                  width: '100%',
                  padding: '0.75rem', 
                  borderRadius: '4px', 
                  border: '1px solid #ced4da',
                  fontSize: '1rem',
                  boxSizing: 'border-box'
                }}
                disabled={loading}
              />
            </div>

            <div className="form-group" style={{ marginBottom: '1.5rem' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '0.5rem', 
                fontWeight: '600',
                color: '#555'
              }}>
                Data sfârșit *
              </label>
              <input
                type="date"
                value={dataSfarsit}
                onChange={(e) => setDataSfarsit(e.target.value)}
                style={{ 
                  width: '100%',
                  padding: '0.75rem', 
                  borderRadius: '4px', 
                  border: '1px solid #ced4da',
                  fontSize: '1rem',
                  boxSizing: 'border-box'
                }}
                disabled={loading}
              />
            </div>

            {error && (
              <div style={{ 
                background: '#f8d7da', 
                color: '#721c24', 
                padding: '0.5rem', 
                borderRadius: '4px', 
                marginBottom: '1rem',
                fontSize: '0.9rem'
              }}>
                {error}
              </div>
            )}

            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button 
                className="btn btn-secondary" 
                onClick={handleCancel}
                disabled={loading}
              >
                Anulează
              </button>
              <button 
                className="btn btn-success" 
                onClick={handleSave}
                disabled={loading || !dataInceput || !dataSfarsit}
              >
                {loading ? 'Se salvează...' : 'Salvează'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OperatorConcedii;
